
import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Calendar as CalendarIcon, Clock } from 'lucide-react';
import { FormControl } from "@/components/ui/form";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import TimePicker from './TimePicker';
import { getDateLocale } from './calendar-utils';

interface DateTimePickerFieldProps {
  value: string | null | undefined;
  onChange: (value: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  clearable?: boolean;
}

const DateTimePickerField: React.FC<DateTimePickerFieldProps> = ({
  value,
  onChange,
  placeholder = "Select date and time",
  disabled = false,
  clearable = true
}) => {
  const { i18n, t } = useTranslation(['admin', 'common']);
  const [date, setDate] = useState<Date | undefined>(value ? new Date(value) : undefined);
  const [hour, setHour] = useState<string>(value ? format(new Date(value), 'HH') : '12');
  const [minute, setMinute] = useState<string>(value ? format(new Date(value), 'mm') : '00');
  const [isOpen, setIsOpen] = useState(false);
  
  // Update local state when value changes from outside
  useEffect(() => {
    if (value) {
      const dateObj = new Date(value);
      setDate(dateObj);
      setHour(format(dateObj, 'HH'));
      setMinute(format(dateObj, 'mm'));
    } else {
      setDate(undefined);
      setHour('12');
      setMinute('00');
    }
  }, [value]);
  
  // Combine date and time
  const updateDateTime = (newDate?: Date, newHour: string = hour, newMinute: string = minute) => {
    if (!newDate) {
      onChange(null);
      return;
    }
    
    const dateObj = new Date(newDate);
    // Ensure we're using integers for hours and minutes
    const h = parseInt(newHour, 10);
    const m = parseInt(newMinute, 10);
    
    if (isNaN(h) || isNaN(m)) {
      console.error('Invalid hour or minute:', newHour, newMinute);
      return;
    }
    
    dateObj.setHours(h, m);
    onChange(dateObj.toISOString());
  };
  
  // Handle time change
  const handleTimeChange = (newHour: string, newMinute: string) => {
    setHour(newHour);
    setMinute(newMinute);
    if (date) {
      updateDateTime(date, newHour, newMinute);
    }
  };
  
  // Handle date change
  const handleDateChange = (newDate?: Date) => {
    setDate(newDate);
    updateDateTime(newDate);
  };
  
  // Handle clear button
  const handleClear = () => {
    setDate(undefined);
    setHour('12');
    setMinute('00');
    onChange(null);
    setIsOpen(false); // Close the popover after clearing
  };
  
  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <FormControl>
          <Button
            variant="outline"
            className={cn(
              "w-full pl-3 text-left font-normal flex justify-between items-center",
              !date && "text-muted-foreground",
              disabled && "opacity-50 cursor-not-allowed"
            )}
            disabled={disabled}
            aria-expanded={isOpen}
            aria-label={placeholder}
          >
            <div className="truncate">
              {date ? (
                format(date, "PPP HH:mm", { locale: getDateLocale(i18n.language) })
              ) : (
                <span>{placeholder}</span>
              )}
            </div>
            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
          </Button>
        </FormControl>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Tabs defaultValue="date">
          <TabsList className="grid grid-cols-2">
            <TabsTrigger value="date" className="flex items-center gap-2">
              <CalendarIcon className="h-4 w-4" />
              <span>{t('common:date')}</span>
            </TabsTrigger>
            <TabsTrigger value="time" className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span>{t('common:time')}</span>
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="date" className="p-0">
            <Calendar
              mode="single"
              selected={date}
              onSelect={handleDateChange}
              initialFocus
              className="p-3 pointer-events-auto"
              locale={getDateLocale(i18n.language)}
            />
          </TabsContent>
          
          <TabsContent value="time" className="p-4">
            <TimePicker 
              hour={hour}
              minute={minute}
              onTimeChange={handleTimeChange}
            />
          </TabsContent>
        </Tabs>
        
        <div className="p-3 border-t flex justify-between">
          {clearable && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleClear}
              aria-label={t('common:clear')}
            >
              {t('common:clear')}
            </Button>
          )}
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => {
              const now = new Date();
              setDate(now);
              setHour(format(now, 'HH'));
              setMinute(format(now, 'mm'));
              updateDateTime(now, format(now, 'HH'), format(now, 'mm'));
            }}
            aria-label={t('common:today')}
          >
            {t('common:today')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default DateTimePickerField;
